/**
 * Push Notifications Service
 * Handles Firebase Cloud Messaging for real-time push notifications on Android/iOS
 * Created: January 2026
 *
 * NOTE: Capacitor plugins loaded dynamically so Vercel web builds succeed.
 */

let _Capacitor = null;
let _PushNotifications = null;

const loadCapacitor = async () => {
    if (_Capacitor) return _Capacitor;
    try {
        const mod = await import(/* webpackIgnore: true */ '@capacitor/core');
        _Capacitor = mod.Capacitor;
        return _Capacitor;
    } catch {
        return null;
    }
};

const loadPush = async () => {
    if (_PushNotifications) return _PushNotifications;
    try {
        const mod = await import(/* webpackIgnore: true */ '@capacitor/push-notifications');
        _PushNotifications = mod.PushNotifications;
        return _PushNotifications;
    } catch {
        return null;
    }
};

/**
 * Check if push notifications are supported
 */
const isPushSupported = async () => {
    const Cap = await loadCapacitor();
    return Cap ? Cap.isNativePlatform() : false;
};

/**
 * Initialize Push Notifications
 * Call this after user login
 */
export const initPushNotifications = async (onTokenReceived) => {
    if (!(await isPushSupported())) {
        return null;
    }

    const PushNotifications = await loadPush();
    if (!PushNotifications) return null;

    try {
        // Request permission
        const permStatus = await PushNotifications.requestPermissions();

        if (permStatus.receive !== 'granted') {
            return null;
        }

        // Register with FCM
        await PushNotifications.register();

        // Listen for registration (FCM token)
        PushNotifications.addListener('registration', (token) => {
            // Callback to send token to backend
            if (onTokenReceived) {
                onTokenReceived(token.value);
            }
        });

        // Listen for registration errors
        PushNotifications.addListener('registrationError', (error) => {
            console.error('[Push] Registration error:', error);
        });

        // Listen for push notifications received (foreground)
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            // You can show a local notification or update UI here
        });

        // Listen for notification tap/action
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            // Handle navigation based on notification data
            const data = action.notification.data;
            if (data?.type === 'approval') {
                window.location.href = '/approvals';
            } else if (data?.type === 'order') {
                window.location.href = '/orders';
            } else if (data?.type === 'visit') {
                window.location.href = '/visits';
            }
        });

        return true;

    } catch (error) {
        console.error('[Push] Initialization error:', error);
        return null;
    }
};

/**
 * Send FCM token to backend for storage
 */
export const sendTokenToBackend = async (token, userId) => {
    try {
        const api = (await import('../utils/api')).default;
        const Cap = await loadCapacitor();

        const response = await api.post('/users/fcm-token', {
            fcm_token: token,
            user_id: userId,
            platform: Cap ? Cap.getPlatform() : 'web'
        });

        if (response.status === 200) {
            return true;
        }
        return false;
    } catch (error) {
        console.error('[Push] Failed to send token:', error.message);
        return false;
    }
};

/**
 * Remove all notification listeners (call on logout)
 */
export const removePushListeners = async () => {
    if (!(await isPushSupported())) return;
    const PushNotifications = await loadPush();
    if (PushNotifications) {
        await PushNotifications.removeAllListeners();
    }
};

export default {
    initPushNotifications,
    sendTokenToBackend,
    removePushListeners,
    isPushSupported
};
